import { inject, Injectable } from '@angular/core';
import { nonNullable } from '@rma/generic/rxjs/operators';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { ChatContextService } from '@rma/public/generic/util/tracking/intercom';
import { combineLatest, filter } from 'rxjs';
import { HotjarScriptService } from './hotjar-script.service';

@Injectable({
  providedIn: 'root',
})
export class HotjarIdentificationService {
  private readonly chatContextService = inject(ChatContextService);
  private readonly currentUserService = inject(PublicUserContextService);
  private readonly hotjarScriptService = inject(HotjarScriptService);

  public initialiseIdentification(): void {
    combineLatest({
      user: this.currentUserService.currentUser$.pipe(
        nonNullable(),
        filter((c) => !!c.userId),
      ),
      userChatData: this.chatContextService.userChatData$,
    }).subscribe(({ user, userChatData }) => {
      const { user_id, user_hash, app_id, ...userProperties } = userChatData;

      // Hotjar has no addUserProperties equivalent, so identify is the only way to attach anything.
      // Impersonated sessions get identified too: the script loads app-wide, so the session is
      // recorded either way and skipping identify would leave it unlabelled rather than excluded.
      // Their chat data is withheld — only the attributes needed to filter the session are sent.
      this.hotjarScriptService.identify(user.userId, {
        ...(user.isImpersonating ? {} : userProperties),
        rma_is_agent: user.userType !== 'Consumer',
        rma_user_type: user.userType,
        'rma.authenticatedViaRadmin': user.isImpersonating,
      });
    });
  }
}
